import { useEffect, useRef } from 'react';
import { useStore } from '../store/useStore';

// Global input state to allow UI components (Joystick) to update it directly
// without causing re-renders or passing refs deep down.
export const inputState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    shift: false,
    attack: false // Left Click + Shift (or just Left Click in some modes)
};

export const useInput = () => {
    useEffect(() => {
        const isTyping = () => {
            const el = document.activeElement as HTMLElement | null;
            if (!el) return false;
            const tag = el.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
        };

        const resetInput = () => {
            inputState.forward = false;
            inputState.backward = false;
            inputState.left = false;
            inputState.right = false;
            inputState.jump = false;
            inputState.shift = false;
            inputState.attack = false;
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            const gameStarted = useStore.getState().gameStarted;
            const isInventoryOpen = useStore.getState().isInventoryOpen;
            const isPaused = useStore.getState().isPaused;
            const isShopOpen = useStore.getState().isShopOpen;
            if (!gameStarted || isInventoryOpen || isPaused || isShopOpen || isTyping()) return;
            
            switch (e.code) {
                case 'KeyW': inputState.forward = true; break;
                case 'KeyS': inputState.backward = true; break;
                case 'KeyA': inputState.left = true; break;
                case 'KeyD': inputState.right = true; break;
                case 'DummyShift': // Placeholder
                case 'ShiftLeft':
                case 'ShiftRight': inputState.shift = true; break;
                case 'Space':
                    if (!e.repeat) inputState.jump = true;
                    break;
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (isTyping()) return;
            switch (e.code) {
                case 'KeyW': inputState.forward = false; break;
                case 'KeyS': inputState.backward = false; break;
                case 'KeyA': inputState.left = false; break;
                case 'KeyD': inputState.right = false; break;
                case 'ShiftLeft':
                case 'ShiftRight': inputState.shift = false; break;
                case 'Space': inputState.jump = false; break;
            }
        };

        const handleMouseDown = (e: MouseEvent) => {
            const gameStarted = useStore.getState().gameStarted;
            const isInventoryOpen = useStore.getState().isInventoryOpen;
            const isPaused = useStore.getState().isPaused;
            const isShopOpen = useStore.getState().isShopOpen;
            if (!gameStarted || isInventoryOpen || isPaused || isShopOpen || isTyping()) return;
            if (e.button === 0) {
                inputState.attack = true;
            }
        };

        const handleMouseUp = (e: MouseEvent) => {
            if (e.button === 0) {
                inputState.attack = false;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('focusin', resetInput);
        window.addEventListener('blur', resetInput);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('focusin', resetInput);
            window.removeEventListener('blur', resetInput);
        };
    }, []);

    return inputState;
};
